import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { parentName: "", childName: "", childAge: "", phone: "", email: "", message: "", trial: false };

export default function Enrollment() {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#trial") {
      setData((d) => ({
        ...d,
        trial: true,
        message: d.message || "I'd like to book a free trial morning for my little one.",
      }));
    }
  }, [location]);

  const set = (k) => (e) => setData({ ...data, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!data.parentName || !data.childName || !data.phone || !data.childAge) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        parent_name: data.parentName,
        child_name: data.childName,
        child_age: data.childAge,
        phone: data.phone,
        message: data.message || undefined,
        trial: !!data.trial,
      };
      if (data.email && data.email.trim()) payload.email = data.email.trim();
      await axios.post(`${API}/enrollments`, payload);
      toast.success(data.trial ? "Trial booked — we'll be in touch! 🐒✨" : "Thank you! We'll be in touch very soon 🐒");
      setData({ ...initial, trial: data.trial });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enroll" className="relative py-20 md:py-28 bg-brand-cream" data-testid="enrollment-section">
      <span id="trial" className="block relative -top-24" aria-hidden />
      <div className="mx-auto max-w-6xl px-6 grid gap-10 md:grid-cols-2 items-start">
        <div>
          <p className="uppercase tracking-widest text-sm font-bold text-brand-green">{data.trial ? "Free Trial Morning" : "Enrollment"}</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-brand-ink">
            {data.trial ? (
              <>Come try a morning <span className="text-brand-red">on us.</span></>
            ) : (
              <>Let&apos;s book your <span className="text-brand-red">little one&apos;s</span> spot.</>
            )}
          </h2>
          <p className="mt-4 text-lg text-brand-ink/80 leading-relaxed">
            {data.trial
              ? "One completely free 3-hour visit — songs, snack, story circle. See if Cheeky Monkeys feels like home for your child before you commit."
              : "Fill in this quick form and one of our teachers will call you back to arrange a warm, friendly tour of the playgroup."}
          </p>

          <button
            type="button"
            onClick={() => setData((d) => ({ ...d, trial: !d.trial }))}
            data-testid="enroll-trial-toggle"
            className={`mt-5 inline-flex items-center gap-2 rounded-full border-2 border-brand-ink px-4 py-2 font-bold text-sm transition-colors ${data.trial ? "bg-brand-green text-white" : "bg-white text-brand-ink hover:bg-brand-yellow/60"}`}
          >
            <Sparkles size={16} /> {data.trial ? "Trial morning selected" : "I'd like a free trial instead"}
          </button>

          <div className="mt-8 rounded-3xl border-[3px] border-brand-ink bg-white p-6 shadow-brutal-sm">
            <p className="font-display text-xl font-semibold text-brand-ink">What happens next?</p>
            <ol className="mt-3 space-y-3">
              {[
                data.trial ? "We'll call you within 1 working day to pick a date." : "We'll call you within 1–2 working days.",
                "Come visit us in Faan Street, Ridgeway.",
                "Meet the teachers, see the space & say hi to the kids.",
                data.trial ? "No pressure — decide after the visit." : "Reserve your child's place for the term.",
              ].map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-brand-yellow border-2 border-brand-ink font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="text-brand-ink/85 leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-3xl border-[3px] border-brand-ink bg-white p-6 md:p-8 shadow-brutal" data-testid="enrollment-form">
          {data.trial && (
            <div className="mb-5 rounded-2xl border-2 border-brand-ink bg-brand-green text-white p-4 flex items-center gap-3" data-testid="trial-banner">
              <Sparkles size={22} fill="#fff" className="shrink-0" />
              <div>
                <p className="font-display font-semibold text-lg leading-tight">Free trial morning</p>
                <p className="text-white/90 text-sm">You&apos;re booking a no-cost visit — no card, no commitment.</p>
              </div>
            </div>
          )}
          <div className="grid gap-4">
            <div>
              <Label htmlFor="parentName" className="font-bold text-brand-ink">Parent&apos;s name *</Label>
              <Input
                id="parentName"
                data-testid="enroll-parent-name"
                value={data.parentName}
                onChange={set("parentName")}
                placeholder="Aisha Khan"
                className="mt-1 h-12 rounded-xl border-2 border-brand-ink focus-visible:ring-brand-red"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="childName" className="font-bold text-brand-ink">Child&apos;s name *</Label>
                <Input
                  id="childName"
                  data-testid="enroll-child-name"
                  value={data.childName}
                  onChange={set("childName")}
                  placeholder="Zaid"
                  className="mt-1 h-12 rounded-xl border-2 border-brand-ink focus-visible:ring-brand-red"
                />
              </div>
              <div>
                <Label className="font-bold text-brand-ink">Child&apos;s age *</Label>
                <Select value={data.childAge} onValueChange={set("childAge")}>
                  <SelectTrigger data-testid="enroll-child-age" className="mt-1 h-12 rounded-xl border-2 border-brand-ink">
                    <SelectValue placeholder="Select age" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">Turning 3 soon</SelectItem>
                    <SelectItem value="3">3 years old</SelectItem>
                    <SelectItem value="4">4 years old</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="font-bold text-brand-ink">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="enroll-phone"
                  value={data.phone}
                  onChange={set("phone")}
                  placeholder="084 000 0000"
                  className="mt-1 h-12 rounded-xl border-2 border-brand-ink focus-visible:ring-brand-red"
                />
              </div>
              <div>
                <Label htmlFor="email" className="font-bold text-brand-ink">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="enroll-email"
                  value={data.email}
                  onChange={set("email")}
                  placeholder="you@email.com"
                  className="mt-1 h-12 rounded-xl border-2 border-brand-ink focus-visible:ring-brand-red"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="message" className="font-bold text-brand-ink">Anything we should know?</Label>
              <Textarea
                id="message"
                data-testid="enroll-message"
                value={data.message}
                onChange={set("message")}
                placeholder="Allergies, questions, preferred start date…"
                className="mt-1 min-h-[110px] rounded-xl border-2 border-brand-ink focus-visible:ring-brand-red"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="enroll-submit-button"
              className={`mt-2 h-14 rounded-full text-white font-bold text-base btn-brutal ${data.trial ? "bg-brand-green hover:bg-brand-greenDark" : "bg-brand-red hover:bg-brand-redDark"}`}
            >
              {loading ? (<><Loader2 size={18} className="animate-spin mr-2" /> Sending…</>) : (<><Send size={18} className="mr-2" /> {data.trial ? "Book my free trial" : "Send enquiry"}</>)}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
